import { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight, ArrowLeft, ArrowRight } from "lucide-react";
import { galleryImages } from "../data";

const slideVariants = {
  enter: (d) => ({ x: d >= 0 ? "100%" : "-100%", opacity: 0.4 }),
  center: { x: 0, opacity: 1 },
  exit: (d) => ({ x: d >= 0 ? "-100%" : "100%", opacity: 0.4 }),
};

const Lightbox = ({ index, onClose, onNav }) => {
  const [dir, setDir] = useState(0);

  const paginate = useCallback(
    (d) => {
      setDir(d);
      onNav(d);
    },
    [onNav]
  );

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") paginate(1);
      if (e.key === "ArrowLeft") paginate(-1);
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose, paginate]);

  const img = galleryImages[index];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      data-testid="lightbox"
      className="fixed inset-0 z-[100] bg-[#0A0A0A]/96 backdrop-blur-sm flex items-center justify-center px-4 sm:px-20"
    >
      <button
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        data-testid="lightbox-close"
        className="absolute top-5 right-5 sm:top-7 sm:right-7 z-[110] w-11 h-11 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
        aria-label="Close"
      >
        <X size={22} strokeWidth={1.5} />
      </button>

      <button
        onClick={(e) => { e.stopPropagation(); paginate(-1); }}
        data-testid="lightbox-prev"
        className="absolute left-2 sm:left-6 z-[110] w-11 h-11 flex items-center justify-center rounded-full text-white/70 hover:text-white hover:bg-white/10 transition-colors"
        aria-label="Previous"
      >
        <ChevronLeft size={34} strokeWidth={1.2} />
      </button>

      <div className="relative w-full max-w-[1000px] h-[78vh] overflow-hidden pointer-events-none">
        <AnimatePresence custom={dir} mode="popLayout">
          <motion.img
            key={index}
            src={img.src}
            alt={img.alt}
            custom={dir}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="absolute inset-0 w-full h-full object-contain grayscale"
            data-testid="lightbox-image"
          />
        </AnimatePresence>
      </div>

      <button
        onClick={(e) => { e.stopPropagation(); paginate(1); }}
        data-testid="lightbox-next"
        className="absolute right-2 sm:right-6 z-[110] w-11 h-11 flex items-center justify-center rounded-full text-white/70 hover:text-white hover:bg-white/10 transition-colors"
        aria-label="Next"
      >
        <ChevronRight size={34} strokeWidth={1.2} />
      </button>

      <span className="absolute bottom-6 left-1/2 -translate-x-1/2 font-body text-xs tracking-[0.25em] text-white/60 tabular-nums">
        {String(index + 1).padStart(2, "0")} / {String(galleryImages.length).padStart(2, "0")}
      </span>
    </motion.div>
  );
};

export const Gallery = () => {
  const [open, setOpen] = useState(false);
  const [index, setIndex] = useState(0);

  const scrollRef = useRef(null);
  const drag = useRef({ down: false, startX: 0, scrollLeft: 0, moved: false });

  const openAt = (i) => { setIndex(i); setOpen(true); };
  const nav = (d) =>
    setIndex((i) => (i + d + galleryImages.length) % galleryImages.length);

  const scrollByCard = (d) => {
    const el = scrollRef.current;
    if (!el) return;
    const card = el.querySelector("[data-carousel-card]");
    const gap = 20;
    const amount = card ? card.offsetWidth + gap : el.clientWidth * 0.5;
    el.scrollBy({ left: d * amount, behavior: "smooth" });
  };

  const onPointerDown = (e) => {
    const el = scrollRef.current;
    drag.current = { down: true, startX: e.pageX, scrollLeft: el.scrollLeft, moved: false };
  };
  const onPointerMove = (e) => {
    if (!drag.current.down) return;
    const dx = e.pageX - drag.current.startX;
    if (Math.abs(dx) > 6) drag.current.moved = true;
    scrollRef.current.scrollLeft = drag.current.scrollLeft - dx;
  };
  const endDrag = () => { drag.current.down = false; };

  const handleCardClick = (i) => {
    if (drag.current.moved) { drag.current.moved = false; return; }
    openAt(i);
  };

  return (
    <section
      id="gallery"
      data-testid="gallery"
      className="py-24 sm:py-32"
    >
      <div className="px-6 sm:px-10 mx-auto max-w-[1600px]">
        <div className="flex items-end justify-between mb-10 sm:mb-12 border-b border-neutral-300 pb-6">
          <h2 className="font-display font-medium tracking-tighter text-4xl sm:text-5xl lg:text-6xl leading-none">
            The space
          </h2>
          <div className="hidden sm:flex items-center gap-3">
            <button
              type="button"
              onClick={() => scrollByCard(-1)}
              data-testid="carousel-prev"
              aria-label="Scroll left"
              className="w-11 h-11 flex items-center justify-center border border-neutral-300 rounded-full hover:bg-neutral-900 hover:text-[#FAFAFA] transition-colors"
            >
              <ArrowLeft size={18} strokeWidth={1.5} />
            </button>
            <button
              type="button"
              onClick={() => scrollByCard(1)}
              data-testid="carousel-next"
              aria-label="Scroll right"
              className="w-11 h-11 flex items-center justify-center border border-neutral-300 rounded-full hover:bg-neutral-900 hover:text-[#FAFAFA] transition-colors"
            >
              <ArrowRight size={18} strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </div>

      <div
        ref={scrollRef}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerLeave={endDrag}
        data-testid="gallery-carousel"
        className="no-scrollbar flex gap-5 overflow-x-auto snap-x snap-proximity cursor-grab active:cursor-grabbing select-none px-6 sm:px-10 pb-2"
        style={{ scrollbarWidth: "none" }}
      >
        {galleryImages.map((img, i) => (
          <button
            key={i}
            type="button"
            data-carousel-card
            data-testid={`gallery-image-${i}`}
            onClick={() => handleCardClick(i)}
            className="group relative shrink-0 snap-start overflow-hidden bg-neutral-200 w-[84vw] sm:w-[66vw] md:w-[52vw] lg:w-[42vw] aspect-square"
          >
            <img
              src={img.src}
              alt={img.alt}
              draggable={false}
              loading="lazy"
              className="w-full h-full object-cover grayscale transition-transform duration-[1.2s] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-105 pointer-events-none"
            />
            <span className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-500" />
            <span className="absolute bottom-4 left-4 font-body text-[10px] tracking-[0.2em] uppercase text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300 mix-blend-difference">
              {String(i + 1).padStart(2, "0")} — View
            </span>
          </button>
        ))}
        <div className="shrink-0 w-1 sm:w-4" aria-hidden="true" />
      </div>

      <AnimatePresence>
        {open && (
          <Lightbox index={index} onClose={() => setOpen(false)} onNav={nav} />
        )}
      </AnimatePresence>
    </section>
  );
};
